import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { m } from 'framer-motion';
import { useState, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha, useTheme, styled } from '@mui/material/styles';
import { CardContent, Box, Card, Typography, Link, Grid, Container } from '@mui/material';
import Markdown from '../../components/Markdown';
// _mock_
import { _appFeatured } from '../../_mock';
// components
import Image from '../../components/Image';
import { MotionContainer, varFade } from '../../components/animate';
import { CarouselDots, CarouselArrows } from '../../components/carousel';

// ----------------------------------------------------------------------

const OverlayStyle = styled('div')(({ theme }) => ({
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 8,
  position: 'absolute',
  backgroundColor: alpha(theme.palette.grey[0], 0),
}));

const RootStyle = styled('div')(({ theme }) => ({
  padding: '15px',
  paddingBottom: '55px',
  textAlign: 'center',
  [theme.breakpoints.up('md')]: {
    textAlign: 'left',
  },
}));

// ----------------------------------------------------------------------

export default function SliderV1({ block }) {
  const theme = useTheme();
  const carouselRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(theme.direction === 'rtl' ? _appFeatured.length - 1 : 0);

  const settings = {
    speed: 1000,
    dots: true,
    arrows: false,
    autoplay: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    rtl: Boolean(theme.direction === 'rtl'),
    beforeChange: (current, next) => setCurrentIndex(next),
    ...CarouselDots({
      zIndex: 9,
      top: 24,
      left: 24,
      position: 'absolute',
    }),
  };

  const handlePrevious = () => {
    carouselRef.current.slickPrev();
  };

  const handleNext = () => {
    carouselRef.current.slickNext();
  };

  return (
    <RootStyle>
      <Container>
        <Grid item xs={12} md={6} lg={7} sx={{ pr: { md: 7 } }}>
          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={6}>
              <Card>
                <Slider ref={carouselRef} {...settings}>
                  {block.slices.map((app, index) => (
                    <CarouselItem key={app.id} item={app} isActive={index === currentIndex} />
                  ))}
                </Slider>

                <CarouselArrows
                  onNext={handleNext}
                  onPrevious={handlePrevious}
                  spacing={0}
                  sx={{
                    top: 16,
                    right: 16,
                    position: 'absolute',
                    '& .arrow': {
                      p: 0,
                      width: 32,
                      height: 32,
                      opacity: 0.48,
                      color: 'common.white',
                      '&:hover': { color: 'common.white', opacity: 1 },
                    },
                  }}
                />
              </Card>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ p: 3 }}>
                <Markdown children={block.description} />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </RootStyle>
  );
}

// ----------------------------------------------------------------------

CarouselItem.propTypes = {
  isActive: PropTypes.bool,
  item: PropTypes.shape({
    description: PropTypes.string,
    image: PropTypes.string,
    title: PropTypes.string,
  }),
};

function CarouselItem({ item, isActive }) {
  const { img, title, description } = item;

  return (
    <Box sx={{ position: 'relative' }}>
      <CardContent
        component={MotionContainer}
        animate={isActive}
        action
        sx={{
          bottom: 0,
          width: 1,
          zIndex: 9,
          textAlign: 'left',
          position: 'absolute',
          color: 'common.white',
        }}
      >
        <m.div variants={varFade().inRight}>
          <Typography variant="overline" component="div" sx={{ mb: 1, opacity: 0.48 }}>
            Morrex Group
          </Typography>
        </m.div>
        <m.div variants={varFade().inRight}>
          <Link component={RouterLink} to="#" color="inherit" underline="none">
            <Typography variant="h5" gutterBottom noWrap>
              {title}
            </Typography>
          </Link>
        </m.div>
        <m.div variants={varFade().inRight}>
          <Typography variant="body2" noWrap>
            {description}
          </Typography>
        </m.div>
      </CardContent>
      <OverlayStyle />
      <Image alt={title} src={img} sx={{ height: { xs: 350, xl: 420 } }} />
    </Box>
  );
}
